import { GetContextMenuItemsParams } from "ag-grid-community";
import omit from "lodash.omit";

import {
  CreateExperienceProductDto,
  ExperienceProductDto,
  ExperienceType,
  GetScriptStatusResponseBodyType,
  ModKeys,
  OfferDto,
  OfferProductDto,
} from "@intelligems/ig-types";
import { AppDispatch } from "@src/store";
import { removeOfferVariationMod } from "@src/store/slices/experienceInfoState";
import { resetVariationOfferProducts } from "@src/store/slices/offerProducts";
import { resetOfferTestOffer } from "@src/store/slices/offers";
import { resetOfferTestOnsiteEdits } from "@src/store/slices/onsiteEdits";
import { resetOfferTestOnsiteInjections } from "@src/store/slices/onsiteInjections";
import { closeProductPicker } from "@src/store/slices/productPicker";
import {
  resetOfferTestRedirects,
  resetRedirectErrors,
} from "@src/store/slices/redirects";
import {
  personalizationOffersGwp,
  personalizationOffersPromotions,
  personalizationOffersShipping,
  personalizationOffersVolumeDiscount,
} from "@src/utils/linkSheet";

export const isCellNull = (p: string, value: string | number) => {
  if (typeof value === "string") {
    return p === "price" ? parseFloat(value) < 0 : parseFloat(value) <= 0;
  } else {
    return p === "price" ? value < 0 : value <= 0;
  }
};

export const getContextMenuItems = (params: GetContextMenuItemsParams) => {
  return (params.defaultItems || []).filter((i) => i !== "export");
};

export const checkHasTemplateSnippet = (
  scriptStatusIsSuccess: boolean,
  scriptStatusData: GetScriptStatusResponseBodyType | undefined,
  experienceType: ExperienceType
) => {
  return (
    experienceType === "content/template" &&
    scriptStatusIsSuccess &&
    scriptStatusData &&
    (scriptStatusData.type === "appEmbedFound" ||
      scriptStatusData.type === "inlineFound" ||
      scriptStatusData.type === "inlineAsyncFound" ||
      scriptStatusData.type === "multipleScriptsWithAppEmbedFound" ||
      scriptStatusData.type === "multipleScriptsFound" ||
      scriptStatusData.type === "missing" ||
      scriptStatusData.type === "incorrectScriptFound") &&
    !scriptStatusData.hasTemplateScript
  );
};

export const differ2 = (function () {
  return {
    VALUE_CREATED: "created",
    VALUE_UPDATED: "updated",
    VALUE_DELETED: "deleted",
    VALUE_UNCHANGED: "unchanged",
    removeUnchanged: function (diff: any) {
      for (const [key, value] of Object.entries(diff)) {
        if (value === this.VALUE_UNCHANGED) {
          delete diff[key];
        } else if (value && typeof value === "object") {
          this.removeUnchanged(value);
        }
      }
      return diff;
    },
    map: function (obj1: any, obj2: any) {
      if (this.isFunction(obj1) || this.isFunction(obj2)) {
        throw new Error("Invalid argument. Function given, object expected.");
      }
      if (this.isValue(obj1) || this.isValue(obj2)) {
        const type = this.compareValues(obj1, obj2);
        return {
          type,
          data: {
            from: obj1,
            to: obj2,
          },
        };
      }

      const diff: any = {};
      for (const key in obj1) {
        if (this.isFunction(obj1[key])) {
          continue;
        }

        let value2 = undefined;
        if (obj2[key] !== undefined) {
          value2 = obj2[key];
        }

        diff[key] = this.map(obj1[key], value2);
      }
      for (const key in obj2) {
        if (this.isFunction(obj2[key]) || diff[key] !== undefined) {
          continue;
        }

        diff[key] = this.map(undefined, obj2[key]);
      }

      return diff;
    },
    compareValues: function (value1: any, value2: any) {
      if (value1 === value2) {
        return this.VALUE_UNCHANGED;
      }
      if (
        this.isDate(value1) &&
        this.isDate(value2) &&
        value1.getTime() === value2.getTime()
      ) {
        return this.VALUE_UNCHANGED;
      }
      if (value1 === undefined) {
        return this.VALUE_CREATED;
      }
      if (value2 === undefined) {
        return this.VALUE_DELETED;
      }
      return this.VALUE_UPDATED;
    },
    isFunction: function (x: any) {
      // @ts-ignore
      return Object.prototype.toString.call(x) === "[object Function]";
    },
    isArray: function (x: any) {
      // @ts-ignore
      return Object.prototype.toString.call(x) === "[object Array]";
    },
    isDate: function (x: any) {
      // @ts-ignore
      return Object.prototype.toString.call(x) === "[object Date]";
    },
    isObject: function (x: any) {
      // @ts-ignore
      return Object.prototype.toString.call(x) === "[object Object]";
    },
    isValue: function (x: any) {
      return !this.isObject(x) && !this.isArray(x);
    },
  };
})();

export const removeUnchangedDiffs2 = (obj: any) => {
  if (
    obj &&
    typeof obj === "object" &&
    "type" in obj &&
    ["created", "updated", "deleted"].includes(obj["type"])
  ) {
    return true;
  }

  let somethingModified = false;
  if (obj && typeof obj === "object") {
    if ("listKey" in obj) {
      obj["listKey"] = obj["listKey"]["data"]["from"];
    }
    for (const [key, value] of Object.entries(obj)) {
      if (value && typeof value === "object") {
        const isModified = removeUnchangedDiffs2(value);
        if (isModified) {
          somethingModified = true;
        }
        if (isModified) {
          continue;
        }
      }
      if (key !== "listKey") {
        delete obj[key];
      }
    }
  }
  return somethingModified;
};

export const convertArraysToObjects3 = (obj: any) => {
  for (const [key, value] of Object.entries(obj)) {
    if (value && Array.isArray(value)) {
      if (value.some((v) => typeof v === "object")) {
        const reduced = value.reduce((acc: any, curr: any, index) => {
          if (typeof curr === "object") {
            const name = curr.name || curr.title || curr.id;
            const id = curr.id || index;
            acc[JSON.stringify({ name, id, key })] =
              convertArraysToObjects3(curr);
            return acc;
          }
          return acc;
        }, {});
        obj[key] = { ...reduced, listKey: key };
      } else if (value.length > 0) {
        const reduced = value.reduce(
          (acc: any, curr: any, currentIndex) => {
            acc[currentIndex] = curr;
            return acc;
          },
          { listKey: key }
        );
        obj[key] = reduced;
      }
    } else if (value && typeof value === "object") {
      obj[key] = convertArraysToObjects3(value);
    }
  }
  return obj;
};

export function sortProductsById(
  products: ExperienceProductDto[] | CreateExperienceProductDto[]
) {
  return products.sort((a, b) => {
    // Convert string IDs to numbers for proper numerical sorting
    const idA = parseInt(a.id);
    const idB = parseInt(b.id);
    return idA - idB;
  });
}

export const formatOfferProductsForDiff = (
  offerProducts: OfferProductDto[]
) => {
  return offerProducts.map((obj) => ({
    ...omit(obj, ["image"]),
    variants: obj.variants.map((variant) =>
      omit(variant, ["price", "compareAtPrice"])
    ),
  }));
};

export const experienceProductsFormatForDiff = (
  products: ExperienceProductDto[] | CreateExperienceProductDto[]
): ExperienceProductDto[] | CreateExperienceProductDto[] => {
  return products.map(
    (product: ExperienceProductDto | CreateExperienceProductDto) => {
      return {
        ...product,
        id: product.id,
        variants: product.variants.map((variant) => {
          return {
            ...variant,
            variationPrices: (variant.variationPrices || []).map((prices) => {
              return {
                ...prices,
                price:
                  typeof prices.price === "string"
                    ? parseFloat(prices.price)
                    : prices.price,
                subscriptionDiscount: prices.subscriptionDiscount ?? null,
                subscriptionDiscountType:
                  prices.subscriptionDiscountType ?? null,
                compareAtPrice:
                  typeof prices.compareAtPrice === "string"
                    ? parseFloat(prices.compareAtPrice)
                    : prices.compareAtPrice === null
                      ? 0
                      : prices.compareAtPrice,
              };
            }),
          };
        }),
      };
    }
  );
};

export const resetVariationMods = (
  variationId: string,
  offerVariationMods: Record<string, { mods: ModKeys[] }>,
  dispatch: AppDispatch
) => {
  const activeMods = offerVariationMods[variationId]?.mods || [];

  activeMods.forEach((mod: ModKeys) => {
    dispatch(removeOfferVariationMod({ variationId, mod }));
  });

  const variationActions = {
    cartDiscount: () => {
      dispatch(resetOfferTestOffer({ variationId }));
      dispatch(closeProductPicker());
      dispatch(resetVariationOfferProducts({ variationId }));
    },
    tieredDiscount: () => {
      dispatch(resetOfferTestOffer({ variationId }));
      dispatch(closeProductPicker());
      dispatch(resetVariationOfferProducts({ variationId }));
    },
    freeGift: () => {
      dispatch(resetOfferTestOffer({ variationId }));
      dispatch(closeProductPicker());
      dispatch(resetVariationOfferProducts({ variationId }));
    },
    splitUrl: () => {
      dispatch(resetRedirectErrors());
      dispatch(resetOfferTestRedirects({ variationId }));
    },
    onsiteInjections: () =>
      dispatch(resetOfferTestOnsiteInjections({ variationId })),
    template: () => {
      dispatch(resetOfferTestRedirects({ variationId }));
    },
    onsiteEdits: () => dispatch(resetOfferTestOnsiteEdits({ variationId })),
    theme: () => {
      dispatch(resetOfferTestRedirects({ variationId }));
    },
  };

  activeMods.forEach((mod) => {
    const action = variationActions[mod];
    if (action) {
      action();
    }
  });
};

export function getDiscountTitle(offer?: OfferDto) {
  if (!offer) return "";
  switch (offer.discountApplicationType) {
    case "cartDiscount":
      switch (offer.discountApplicationMethod) {
        case "perItem":
          return "Amount off products";
        case "perOrder":
          return "Amount off order";
        default:
          return "Amount off";
      }
    case "tieredDiscount":
      return "Volume Discount";
    case "freeGift":
      return "Free Gift";
    case "freeShipping":
      return "Free Shipping";
    default:
      return "Offer";
  }
}

export function getDiscountDocLink(linkDescription: string, offer?: OfferDto) {
  switch (offer?.discountApplicationType) {
    case "cartDiscount":
      return { link: personalizationOffersPromotions, linkDescription };
    case "tieredDiscount":
      return { link: personalizationOffersVolumeDiscount, linkDescription };
    case "freeGift":
      return { link: personalizationOffersGwp, linkDescription };
    case "freeShipping":
      return { link: personalizationOffersShipping, linkDescription };
  }
}

export function getDiscountDescription(offer?: OfferDto) {
  switch (offer?.discountApplicationType) {
    case "cartDiscount":
      switch (offer.discountApplicationMethod) {
        case "perItem":
          return "Amount off products";
        case "perOrder":
          return "Amount off order";
        default:
          return "Amount off";
      }
    case "tieredDiscount":
      return "Volume discount";
    case "freeGift":
      return "Free gift";
    case "freeShipping":
      return "Free shipping";

    default:
      return "";
  }
}
